import { useEffect, useState, type CSSProperties } from 'react';
import {
  closeNightRun,
  continueAfterHours,
  hallmarkCandidates,
  nextTrayRare,
  setHallmark,
  type NightRun,
  type Profile,
} from '../../../campaign';
import { t } from '../../../i18n';
import { itemName } from '../../../app/describe';
import { sounds } from '../../../app/sound';
import { PawnFigure } from '../PawnFigure';
import { formatScore } from '../ScoreBurst';
import { BrokerHand, BrokerLine, Candle, lineFor } from './Broker';
import { mantelObject, showOffer } from './Item';
import { NightObject } from './Objects';
import { ShareSheet } from './ShareCard';
import { Crossing } from './SpoilsScene';
import { nightLabel, roman } from './runText';

/**
 * The end of a run (docs/LONG_NIGHT_ART_DIRECTION.md §11.18–§11.21).
 *
 * Won: dawn, the only time the void is lit from below; the candle goes out on its own, no longer
 * needed; the pawns brought home walk across the board, shard to orb, a gold seam each. From Term
 * III, the Hallmark: a pawn is chosen and its Carving struck into it with one heavy stamp. Then
 * Leave at dawn, or Stay for After Hours.
 *
 * Lost: a whole second of silence while the lamp gutters twice and goes out (the scene's); his
 * hands come down and gather the marked pawns one at a time, each tag turning to his ink; marked
 * stand-ins simply fade. One quiet line from him, never mockery.
 *
 * Then the run's summary, a single page of the Ledger written one line per beat.
 */
export function RunEndScene({
  profile,
  run,
  apply,
  reduceMotion,
}: {
  profile: Profile;
  run: NightRun;
  apply: (fn: (p: Profile) => Profile) => void;
  reduceMotion: boolean;
}) {
  const l = profile.ledger!;
  const won = run.phase === 'won';
  const last = run.outcomes[run.outcomes.length - 1];
  const hallmark = won && l.hallmarkPending && hallmarkCandidates(profile).length > 0;
  const [chosenStage, setStage] = useState<'moment' | 'hallmark' | 'summary'>(
    reduceMotion ? (hallmark ? 'hallmark' : 'summary') : 'moment',
  );
  // Once the Hallmark is struck (or declined), the page is written.
  const stage = chosenStage === 'hallmark' && !hallmark ? 'summary' : chosenStage;
  const [sharing, setSharing] = useState(false);
  const name = (id: string) => (profile.pawns[id]?.name ?? id).split(' ')[0]!;
  const next = () => setStage(hallmark ? 'hallmark' : 'summary');
  const reclaimed = won ? (last?.reclaimed ?? []) : [];
  const gathered = won ? [] : (last?.lost ?? []);
  const faded = won ? [] : (last?.vanished ?? []);

  // How long the moment holds before the page is written.
  useEffect(() => {
    if (stage !== 'moment') return;
    const ms = won ? 3400 + reclaimed.length * 180 : 1400 + gathered.length * 500 + 2600;
    const id = setTimeout(() => setStage(hallmark ? 'hallmark' : 'summary'), ms);
    return () => clearTimeout(id);
  }, [stage, won, hallmark, reclaimed.length, gathered.length]);

  return (
    <div className={`ln-screen re-scene has-dock ${won ? 'is-won' : 'is-lost'}`}>
      {stage === 'moment' && won && (
        <div className="re-dawn" onClick={next}>
          <span className="re-candle">
            <Candle night={run.night} size={56} />
          </span>
          <h1 className="sp-title">{t('end.dawn')}</h1>
          {reclaimed.length > 0 && <Crossing ids={reclaimed} name={name} />}
          <BrokerLine text={lineFor('closingLost')} still={reduceMotion} onDone={() => undefined} />
        </div>
      )}
      {stage === 'moment' && !won && (
        <div className="re-lost" onClick={next}>
          <h1 className="sr-only">{t('night.runLost')}</h1>
          <div className="re-gather">
            {gathered.map((id, i) => (
              <span key={id} className="re-pawn" style={{ '--d': i } as CSSProperties}>
                <BrokerHand pose="rest" at={0.5} still={reduceMotion} />
                <span className="re-figure">
                  <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
                </span>
                <span className="re-tag">{name(id)}</span>
              </span>
            ))}
            {faded.map((id, i) => (
              <span
                key={id}
                className="re-pawn is-stand-in"
                style={{ '--d': gathered.length + i } as CSSProperties}
              >
                <span className="re-figure">
                  <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
                </span>
              </span>
            ))}
          </div>
          <BrokerLine
            key="line"
            text={lineFor('runLost')}
            still={reduceMotion}
            onDone={() => undefined}
          />
        </div>
      )}
      {stage === 'hallmark' && (
        <Hallmark profile={profile} run={run} apply={apply} reduceMotion={reduceMotion} />
      )}
      {stage === 'summary' && (
        <>
          <Summary profile={profile} run={run} />
          <button type="button" className="btn re-share" onClick={() => setSharing(true)}>
            {t('end.share')}
          </button>
        </>
      )}
      {sharing && <ShareSheet run={run} onClose={() => setSharing(false)} />}

      <div className="ln-dock">
        {stage === 'summary' ? (
          won ? (
            <>
              {l.unlocked.includes('after-hours') && (
                <button
                  type="button"
                  className="btn re-quiet"
                  onClick={() => apply(continueAfterHours)}
                >
                  {t('night.toAfterHours')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary btn-brass btn-large ln-dock-main"
                autoFocus
                onClick={() => apply(closeNightRun)}
              >
                {t('end.leaveAtDawn')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-brass btn-large ln-dock-main"
              autoFocus
              onClick={() => apply(closeNightRun)}
            >
              {t('end.newRun')}
            </button>
          )
        ) : stage === 'moment' ? (
          <button type="button" className="btn btn-large ln-dock-main" onClick={next}>
            {t('action.next')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The Hallmark (§11.18), from Term III: you choose the pawn, and its Carving is struck into it as
 * a small gold punch mark, with one heavy stamp: the only slam outside the Tally.
 */
function Hallmark({
  profile,
  run,
  apply,
  reduceMotion,
}: {
  profile: Profile;
  run: NightRun;
  apply: (fn: (p: Profile) => Profile) => void;
  reduceMotion: boolean;
}) {
  const candidates = hallmarkCandidates(profile);
  const [pawn, setPawn] = useState<string | null>(null);
  const [carving, setCarving] = useState<string | null>(null);
  const [struck, setStruck] = useState(false);
  const carvings = pawn ? (run.carvings[pawn] ?? []) : [];
  const chosen = carving ?? (carvings.length === 1 ? carvings[0]!.id : null);
  return (
    <div className="re-hallmark">
      <h1 className="sp-title">{t('night.hallmark')}</h1>
      <p className="sp-lead">{t('night.hallmarkHint')}</p>
      <div className="re-pawns">
        {candidates.map((id) => (
          <button
            key={id}
            type="button"
            className={`sh-pawn ${pawn === id ? 'is-chosen' : ''}`}
            aria-pressed={pawn === id}
            disabled={struck}
            onClick={() => {
              setPawn(id);
              setCarving(null);
            }}
          >
            <span className="sh-figure">
              <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
              {pawn === id && struck && <span className="re-punch" aria-hidden="true" />}
            </span>
            <span className="fl-tag">{(profile.pawns[id]?.name ?? id).split(' ')[0]}</span>
          </button>
        ))}
      </div>
      {carvings.length > 1 && (
        <div className="dt-modes">
          {carvings.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`btn btn-small ${chosen === c.id ? 'is-on' : ''}`}
              aria-pressed={chosen === c.id}
              disabled={struck}
              onClick={() => setCarving(c.id)}
            >
              {itemName(c.id)}
            </button>
          ))}
        </div>
      )}
      <div className="ln-choice re-hallmark-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!pawn || !chosen || struck}
          onClick={() => {
            const id = pawn!;
            const c = chosen!;
            sounds.play('slamBig');
            if (reduceMotion) return apply((p) => setHallmark(p, id, c));
            setStruck(true);
            setTimeout(() => apply((p) => setHallmark(p, id, c)), 900);
          }}
        >
          {t('night.hallmarkSet')}
        </button>
        <button
          type="button"
          className="btn"
          disabled={struck}
          onClick={() => apply((p) => setHallmark(p, null, null))}
        >
          {t('night.hallmarkSkip')}
        </button>
      </div>
    </div>
  );
}

/**
 * The run's summary (§11.21): a single page of the Ledger, its lines written in ink one per beat
 * (180 ms each). How far the run went (the candle, burnt down to where it stopped), the best chain
 * in gold, the Superstition that fired most, how close the last table was, the Rare that was in
 * the next tray (a lost run), and the pawns lost and brought home.
 */
function Summary({ profile, run }: { profile: Profile; run: NightRun }) {
  const won = run.phase === 'won';
  const last = run.outcomes.filter((o) => !o.skipped).at(-1);
  const name = (id: string) => (profile.pawns[id]?.name ?? id).split(' ')[0]!;
  const fired = Object.entries(run.counters)
    .filter(([k]) => k.startsWith('fired:'))
    .sort((a, b) => b[1] - a[1])[0];
  const rare = won ? null : safe(() => nextTrayRare(profile));
  const lost = [...new Set(run.outcomes.flatMap((o) => o.lost))];
  const home = [...new Set(run.outcomes.flatMap((o) => o.reclaimed))];
  const top = last ? Math.max(1, last.scores.S, last.scores.N) : 1;
  let line = 0;
  const beat = () => ({ '--d': line++ }) as CSSProperties;
  return (
    <article className="re-page">
      <header className="re-head" style={beat()}>
        <span className="re-candle">
          <Candle night={run.night} size={40} />
        </span>
        <span>
          <span className="ln-eyebrow">
            {itemName(`pledge.${run.pledge}`)} · {t('night.termN', { n: roman(run.term) })}
          </span>
          <h1 className="re-title">{won ? t('night.runWon') : t('night.runLost')}</h1>
          <span className="re-far">{t('end.reached', { night: nightLabel(run.night) })}</span>
        </span>
      </header>
      <dl className="re-lines">
        <div className="re-line" style={beat()}>
          <dt>{t('end.bestChain')}</dt>
          <dd className="re-gold">{formatScore(run.bestChain)}</dd>
        </div>
        {fired && (
          <div className="re-line" style={beat()}>
            <dt>{t('end.firedMost', { n: fired[1] })}</dt>
            <dd className="re-item">
              <NightObject
                spec={mantelObject({ uid: fired[0], id: fired[0].slice(6) })}
                size={34}
              />
              <span>{itemName(fired[0].slice(6))}</span>
            </dd>
          </div>
        )}
        {last && (
          <div className="re-line" style={beat()}>
            <dt>{t('end.lastTable')}</dt>
            <dd className="re-bars">
              <span
                className="re-bar is-you"
                style={{ '--w': last.scores.S / top } as CSSProperties}
              >
                {formatScore(last.scores.S)}
              </span>
              <span
                className="re-bar is-his"
                style={{ '--w': last.scores.N / top } as CSSProperties}
              >
                {formatScore(last.scores.N)}
              </span>
            </dd>
          </div>
        )}
        {rare && (
          <div className="re-line" style={beat()}>
            <dt>{t('end.nextTray')}</dt>
            <dd className="re-item re-glass">
              {showOffer(rare).object && <NightObject spec={showOffer(rare).object!} size={40} />}
              <span>{showOffer(rare).name}</span>
            </dd>
          </div>
        )}
        {lost.length > 0 && (
          <div className="re-line" style={beat()}>
            <dt>{t('end.lostPawns')}</dt>
            <dd className="re-pawnlist">
              {lost.map((id) => (
                <span key={id} className="re-mini is-his">
                  <PawnFigure side="N" crowned={false} immune={false} inscription={null} />
                  <i>{name(id)}</i>
                </span>
              ))}
            </dd>
          </div>
        )}
        {home.length > 0 && (
          <div className="re-line" style={beat()}>
            <dt>{t('end.homePawns')}</dt>
            <dd className="re-pawnlist">
              {home.map((id) => (
                <span key={id} className="re-mini">
                  <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
                  <span className="sp-seam is-still" aria-hidden="true" />
                  <i>{name(id)}</i>
                </span>
              ))}
            </dd>
          </div>
        )}
      </dl>
    </article>
  );
}

function safe<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}
